import * as Styled from './PunchupTicketTypesList.styles';

import { DragDropContext, Draggable, DropResult, Droppable } from '@hello-pangea/dnd';
import { useHistory, useParams } from 'react-router-dom';

import { DragHandle } from 'assets/customizedIcons';
import { FC } from 'react';
import { IdParamsType } from 'utils/types';
import { ROUTES } from 'features/routes';
import { TicketTypesListProps } from './types';
import { formatMoney } from 'utils/utils';
import { DateTime } from 'luxon';
import { ChevronRight } from 'lucide-react';
import { ToolTip } from 'features/common/components/InfoTooltip';
import useGetEvent from 'features/common/hooks/useGetEvent';

const PunchupTicketTypesList: FC<TicketTypesListProps> = ({ ticketTypes, setTicketTypes, showSaveButtons }) => {
	const { id: eventId } = useParams<IdParamsType>();
	const { event } = useGetEvent();
	const timezone = event?.timeZone || 'UTC';
	const { push } = useHistory();

	const onDragEnd = (result: DropResult) => {
		if (!result.destination) return;

		const newTicketTypes = Array.from(ticketTypes.sort((tt1, tt2) => tt1.orderIndex - tt2.orderIndex));
		const [reorderedItem] = newTicketTypes.splice(result.source.index, 1);
		newTicketTypes.splice(result.destination.index, 0, reorderedItem);
		newTicketTypes.forEach((tt, index) => (tt.orderIndex = index));

		setTicketTypes(newTicketTypes);
	};

	const onClickDetails = (ticketId: number) => {
		push(ROUTES.TICKET_DETAILS_PATH.replace(':id', eventId).replace(':ticketId', ticketId.toString()));
	};

	const formatSellingTime = (sellingTime?: string, offSellingTime?: string) => {
		if (!sellingTime) return 'On sale';
		try {
			const now = DateTime.now().setZone(timezone);
			const sellingDate = DateTime.fromISO(sellingTime, { zone: 'UTC' }).setZone(timezone);
			const offSellingDate = DateTime.fromISO(offSellingTime || '', { zone: 'UTC' }).setZone(timezone);
			const onSale = sellingDate < now;

			return onSale
				? `On sale - Ends ${offSellingDate.toFormat('MMM dd, yyyy')} at ${offSellingDate.toFormat('h:mm a')}`
				: `Not on sale - Starts ${sellingDate.toFormat('MMM dd, yyyy')} at ${sellingDate.toFormat('h:mm a')}`;
		} catch {
			return 'On sale';
		}
	};

	return (
		<DragDropContext onDragEnd={onDragEnd}>
			<Droppable droppableId="droppable" direction="vertical">
				{(provided) => (
					<Styled.DraggableContainer ref={provided.innerRef} {...provided.droppableProps}>
						{ticketTypes.map(
							(
								{
									iD,
									initialPrice,
									amount,
									sold,
									holdsAmount,
									name,
									ticketsPool,
									sellingTime,
									offSellingTime,
									ticketVisibility,
									payWhatYouWant,
								},
								index
							) => (
								<Draggable key={iD} draggableId={iD.toString()} index={index}>
									{(provided) => (
										<Styled.TicketCardContainer ref={provided.innerRef} {...provided.draggableProps}>
											<Styled.CardContent>
												<Styled.LeftSection>
													<Styled.DragHandleContainer {...provided.dragHandleProps} data-cy="drag-handle">
														<DragHandle />
													</Styled.DragHandleContainer>

													<Styled.TicketInfo>
														<Styled.TitleRow>
															<Styled.TooltipWrapper>
																<ToolTip
																	text={`${name} - ${
																		payWhatYouWant ? 'Pay what you want' : `$${formatMoney(initialPrice)}`
																	}`}
																>
																	<Styled.TicketName>
																		{name} - {payWhatYouWant ? 'Pay what you want' : `$${formatMoney(initialPrice)}`}
																	</Styled.TicketName>
																</ToolTip>
															</Styled.TooltipWrapper>
														</Styled.TitleRow>

														<Styled.InfoRow>
															{ticketVisibility ? (
																<Styled.InfoText>{formatSellingTime(sellingTime, offSellingTime)}</Styled.InfoText>
															) : (
																<Styled.InfoText>Not on sale</Styled.InfoText>
															)}
														</Styled.InfoRow>

														<Styled.InfoRow>
															<Styled.InfoText>
																{sold}/{!ticketsPool ? amount : ticketsPool.totalAmount || 0} tickets - {holdsAmount}{' '}
																comped
															</Styled.InfoText>
														</Styled.InfoRow>
													</Styled.TicketInfo>
												</Styled.LeftSection>
												<Styled.ActionButton onClick={() => !showSaveButtons && onClickDetails(iD)}>
													<ChevronRight size={22} />
												</Styled.ActionButton>
											</Styled.CardContent>
										</Styled.TicketCardContainer>
									)}
								</Draggable>
							)
						)}
						{provided.placeholder}
					</Styled.DraggableContainer>
				)}
			</Droppable>
		</DragDropContext>
	);
};

export { PunchupTicketTypesList };
